import { Injectable } from '@angular/core';
import { PlanningPeriod } from '@digiwall/goliath-documents/proxy';
import { PlanningBoardFilterState } from '../planning-common/filters';

/** Persisted view preferences for one planning board (zoom, position, quick-search, weekend, filters). */
export interface PlanningViewState {
  /** Selected zoom range (Day/Week/Month). */
  range?: PlanningPeriod;
  /** ISO yyyy-MM-dd of the displayed window's anchor date. */
  anchor?: string;
  /** Quick-search text. */
  search?: string;
  /** Whether weekend day columns are shown (only relevant at daily granularity). */
  showWeekends?: boolean;
  /** Whether all columns are fit into the viewport (no horizontal scroll). */
  fitToScreen?: boolean;
  /** Active board filter selections per data source. */
  filters?: Record<string, PlanningBoardFilterState>;
}

const STORAGE_PREFIX = 'planning-view-state:';

/**
 * Per-user, per-board view state persisted in localStorage as a single JSON object keyed by the routed
 * `configId`. Restores the last zoom, displayed date, quick-search and weekend toggle on reload, kept
 * local to the browser. Mirrors {@link PlanningCardFieldVisibilityStore}; provided at the board
 * component level so state lives with the open board.
 */
@Injectable()
export class PlanningViewStateStore {
  private storageKey = '';
  private current: PlanningViewState = {};

  /** Binds the store to a board and returns that board's persisted view state (empty if none). */
  init(configId: string): PlanningViewState {
    this.storageKey = `${STORAGE_PREFIX}${configId}`;
    this.current = this.read();
    return this.current;
  }

  /** Merges the given fields into the stored state and persists. */
  save(state: PlanningViewState): void {
    this.current = { ...this.current, ...state };
    this.persist(this.current);
  }

  private read(): PlanningViewState {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? (JSON.parse(raw) as PlanningViewState) : {};
    } catch {
      return {};
    }
  }

  private persist(state: PlanningViewState): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch {
      // Best-effort convenience — ignore quota/serialization failures.
    }
  }
}
